import { isEmpty, isNil } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import {
  addPromptTestHelper,
  deletePromptTestHelper,
  reusablePromptsHelper,
  updatePromptTestHelper,
} from '@/reducers/prompts/prompts.helpers'
import {
  setPrompts,
  setPristinePrompts,
  setLoading,
  setModelOptions,
  setPersonaOptions,
} from './prompts.redux'

const formatModelOptions = (models) =>
  (models?.llm_model_options || []).map((option) => ({
    name: option.name,
    value: option.llm_model_id,
    label: option.display_name,
    description: option.description,
    deprecated: option.deprecated,
    reasoning_effort: option.reasoning_effort || null,
  }))

export const fetchModels = () => async (dispatch) => {
  dispatch(setLoading({ modelOptions: true }))

  try {
    const [qaCopilotModels, copilotFallbackModels] = await Promise.all([
      fetchingAPI(`${apiService.summary}/models?feature=qa_copilot`, 'GET', dispatch),
      fetchingAPI(`${apiService.summary}/models?feature=copilot_fallback`, 'GET', dispatch),
    ])

    dispatch(
      setModelOptions({
        qa_copilot: formatModelOptions(qaCopilotModels),
        copilot_fallback: formatModelOptions(copilotFallbackModels),
      })
    )
  } catch (err) {
    toast.error('Failed to fetch models', { cause: err })
  } finally {
    dispatch(setLoading({ modelOptions: false }))
  }
}

export const fetchPrompts =
  (promptIds, promptIdToScid = {}) =>
  async (dispatch, getState) => {
    dispatch(setLoading({ prompts: true }))
    // A scorecard save already returned the prompts it wrote, so the reload it navigates into only has to GET the
    // ones we aren't holding yet — on a cold page load, that is still all of them.
    const { prompts: storedPrompts, pristinePrompts } = getState().prompts
    const reusablePrompts = reusablePromptsHelper(promptIds, storedPrompts, pristinePrompts)
    const idsToFetch = promptIds.filter(
      (promptId) => !reusablePrompts.some((prompt) => prompt.id === promptId)
    )

    try {
      const fetchedPrompts = await Promise.all(
        idsToFetch.map((promptId) =>
          fetchingAPI(`${apiService.summary}/prompts/${promptId}`, 'GET', dispatch)
        )
      )
      // Associate prompts with their criteria's scid so they can be matched
      // after page reload (prompts from server don't have scid by default)
      const promptsWithScid = [...reusablePrompts, ...fetchedPrompts].map((prompt) => ({
        ...prompt,
        scid: promptIdToScid[prompt.id] || prompt.scid,
      }))
      dispatch(setPrompts(promptsWithScid))
      // Kept as the baseline a save diffs against, so an unrelated scorecard edit doesn't version every prompt.
      dispatch(setPristinePrompts(promptsWithScid))
    } catch (err) {
      toast.error('Failed to fetch prompts', { cause: err })
    } finally {
      dispatch(setLoading({ prompts: false }))
    }
  }

export const createPromptTest = (prompt, promptTest) => async (dispatch, getState) => {
  try {
    const formattedPromptTest = {
      ...promptTest,
      inputs: {
        ...(promptTest.inputs || {}),
        call_type: (promptTest.inputs && promptTest.inputs.call_type) || 'call',
      },
      action: 'create',
    }
    const body = JSON.stringify(formattedPromptTest)
    const promptTestResponse = await fetchingAPI(
      `${apiService.summary}/prompts/${prompt.id}/tests`,
      'POST',
      dispatch,
      body
    )
    // Read the list at dispatch time, not from the caller: with several saves in flight
    // (bulk save-to-prompt), a render-time snapshot predates the earlier saves and
    // rebuilding from it clobbers their tests in the store.
    const currentPrompts = getState().prompts.prompts
    const currentPrompt =
      currentPrompts.find(
        (candidate) =>
          (candidate.id && candidate.id === prompt.id) ||
          (candidate.scid && candidate.scid === prompt.scid)
      ) || prompt
    dispatch(setPrompts(addPromptTestHelper(currentPrompts, currentPrompt, promptTestResponse)))
  } catch (err) {
    toast.error('Failed to create test', { cause: err })
  }
}

export const deletePromptTest = (prompts, prompt, promptTestId) => async (dispatch) => {
  try {
    const promptTestResponse = await fetchingAPI(
      `${apiService.summary}/prompts/${prompt.id}/tests/${promptTestId}`,
      'DELETE',
      dispatch
    )
    dispatch(setPrompts(deletePromptTestHelper(prompts, prompt, promptTestResponse)))
  } catch (err) {
    toast.error('Failed to delete test', { cause: err })
  }
}

export const getPromptTestResults = (promptId, tempPrompt) => async (dispatch) => {
  dispatch(setLoading({ promptTests: true }))
  try {
    const formattedPrompt = {
      ...tempPrompt,
      inputs: {
        ...(tempPrompt.inputs || {}),
        call_type: (tempPrompt.inputs && tempPrompt.inputs.call_type) || 'call',
      },
      action: 'execute',
    }
    const body = JSON.stringify(formattedPrompt)
    return await fetchingAPI(
      `${apiService.summary}/prompts/${promptId}/tests`,
      'POST',
      dispatch,
      body
    )
  } catch (err) {
    toast.error('Failed to run tests', { cause: err })
    return []
  } finally {
    dispatch(setLoading({ promptTests: false }))
  }
}

export const getSinglePromptTestResults =
  (promptId, tempPrompt, expectedResponse, callId, description) => async (dispatch) => {
    dispatch(setLoading({ promptTests: true }))
    try {
      const formattedPrompt = {
        ...tempPrompt,
        action: 'execute',
        expected_response: expectedResponse,
        inputs: {
          call_id: callId,
          call_type: (tempPrompt.inputs && tempPrompt.inputs.call_type) || 'call',
        },
        description,
      }
      const body = JSON.stringify(formattedPrompt)

      return await fetchingAPI(
        `${apiService.summary}/prompts/${promptId}/temp_tests`,
        'POST',
        dispatch,
        body
      )
    } catch (err) {
      toast.error('Failed to run tests', { cause: err })
      return null
    } finally {
      dispatch(setLoading({ promptTests: false }))
    }
  }

export const updatePromptTest = (promptId, promptTestId, updates) => async (dispatch, getState) => {
  try {
    const body = JSON.stringify(updates)

    await fetchingAPI(
      `${apiService.summary}/prompts/${promptId}/tests/${promptTestId}`,
      'PUT',
      dispatch,
      body
    )

    // Update the store
    const { prompts } = getState().prompts
    dispatch(setPrompts(updatePromptTestHelper(prompts, promptId, promptTestId, updates)))
    toast.success('Test case updated')
  } catch (err) {
    toast.error('Failed to update test case', { cause: err })
  }
}

// Legacy alias for backwards compatibility
export const updatePromptTestDescription =
  (promptId, promptTestId, newDescription) => async (dispatch) => {
    return dispatch(updatePromptTest(promptId, promptTestId, { description: newDescription }))
  }

export const updatePrompt = (promptId, updates) => async (dispatch) => {
  try {
    const body = JSON.stringify(updates)

    const updatedPrompt = await fetchingAPI(
      `${apiService.summary}/prompts/${promptId}`,
      'PUT',
      dispatch,
      body
    )

    return updatedPrompt
  } catch (err) {
    toast.error('Failed to update prompt', { cause: err })
    throw err
  }
}

export const fetchPersonaList = () => async (dispatch) => {
  dispatch(setLoading({ personaOptions: true }))
  try {
    const res = await fetchingAPI(
      `${apiService.summary}/prompts/generator/persona-list`,
      'GET',
      dispatch
    )
    const data = typeof res?.json === 'function' ? await res.json() : res

    // Backend shape: { personas: [{ key, value, label }, ...] }
    const optionsRaw = Array.isArray(data?.personas) ? data.personas : []
    const options = optionsRaw.map((p, i) => ({
      key: p.key ?? `persona_${i}`,
      value: p.key ?? `persona_${i}`,
      label: p.label ?? p.value ?? p.key ?? `Persona ${i + 1}`,
    }))

    dispatch(setPersonaOptions(options))
  } catch (err) {
    toast.error('Failed to fetch persona list', { cause: err })
  } finally {
    dispatch(setLoading({ personaOptions: false }))
  }
}

/**
 * Find a call that matches specific criteria for testing prompts
 * @param {string} criteriaScid - The criteria SCID to search for
 * @param {string} expectedScore - Expected score ('yes', 'no', 'n/a')
 * @param {number|null} organizationId - Optional organization ID for cross-org access
 * @param {number[]} excludeCallIds - Call IDs to exclude from results
 * @returns {Promise<{call_id: number, call_type: string}|null>}
 */
export const findTestCall =
  (criteriaScid, expectedScore, organizationId = null, excludeCallIds = []) =>
  async (dispatch) => {
    const params = new URLSearchParams({
      criteria_scid: criteriaScid,
      expected_score: expectedScore,
    })

    if (organizationId) {
      params.append('requested_organization_id', organizationId)
    }

    if (!isEmpty(excludeCallIds)) {
      params.append('exclude_call_ids', excludeCallIds.join(','))
    }

    const result = await fetchingAPI(
      `${apiService.workflow}/tasks/prompt-feedback/find-test-call?${params}`,
      'GET',
      dispatch
    )

    return result
  }

export const explainCopilotPrompt =
  ({ prompt, promptId }) =>
  async (dispatch) => {
    try {
      const res = await fetchingAPI(
        `${apiService.summary}/prompts/generator/explain-prompt/`,
        'POST',
        dispatch,
        JSON.stringify(isNil(prompt) ? { prompt_id: promptId } : { prompt })
      )
      const data = typeof res?.json === 'function' ? await res.json() : res
      return data?.how_to_pass ? data : null
    } catch (err) {
      return null
    }
  }

export const generateCopilotPrompt =
  (persona, question, context, applicable, notApplicable, passExample) => async (dispatch) => {
    dispatch(setLoading({ generatePrompt: true }))

    // tiny helpers
    const cleanPrompt = (s) => {
      if (!s || typeof s !== 'string') {
        return ''
      }
      let out = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim()
      out = out.replace(/^\s*\n+/, '').replace(/\n+\s*$/, '')
      out = out.replace(/\n{3,}/g, '\n\n')
      out = out.replace(/[“”]/g, '"').replace(/['']/g, "'")
      return out
    }

    try {
      const body = JSON.stringify({
        persona, // enum key, e.g. 'qa' or 'eligibility'
        question,
        context,
        applicable,
        not_applicable: notApplicable,
        pass_example: passExample,
      })

      const res = await fetchingAPI(
        `${apiService.summary}/prompts/generator/generate-prompt/`,
        'POST',
        dispatch,
        body
      )
      const data = typeof res?.json === 'function' ? await res.json() : res
      // { "generated_prompt": "{\"qa-copilot-prompt\":\"...\"}" }
      const inner = JSON.parse(data.generated_prompt)
      const raw = inner['qa-copilot-prompt'] || ''
      const generated_prompt = cleanPrompt(raw)

      return { generated_prompt }
    } catch (err) {
      toast.error('Failed to generate prompt', { cause: err })
      return null
    } finally {
      dispatch(setLoading({ generatePrompt: false }))
    }
  }
